import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DA_QuantityPicker } from "@molecules/QuantityPicker/QuantityPicker.component";
import { DA_CartButton } from "@molecules/CartButton/CartButton.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_BasicLink } from "@danishagro/shared/src/components/atoms/BasicLink/BasicLink.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { useBasket } from "@danishagro/shared/src/contexts/basket/basket.context";
import { useNavigate } from "react-router-dom";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import {
    DA_NewProduct,
    DA_NewProductSimple,
    VariantGroup,
} from "@danishagro/shared/src/interfaces/productv2.interface";
import { ModalSize, useModal } from "@danishagro/shared/src/contexts/modal.context";
import { DA_DeliveryInfoModalContent } from "@templates/DeliveryInfoModal/DeliveryInfoModal.component";
import { useProductTracker } from "@danishagro/shared/src/gtm/useProductsTracker";
import { DA_InlineLink } from "@danishagro/shared/src/components/atoms/InlineLink/InlineLink.component";
import { B2bImageSrc } from "@danishagro/shared/src/helpers/imageSrc.helper";
import { ImageConfig } from "@danishagro/shared/src/content/imageConfigs/imageConfig.enum";
import { useBottomSheet } from "@danishagro/shared/src/contexts/bottomSheet/bottomSheet.context";
import { DA_QuantityOnPalletOverlay } from "@danishagro/shared/src/modals/QuantityOnPalletShop/Overlay/Overlay.component";
import { DA_Modal } from "@danishagro/shared/src/components/molecules/Modal/Modal.component";
import { modalState } from "@danishagro/shared/src/enums/modalState.enum";
import { BottomSheetDeliveryInfo } from "@organisms/BottomSheetDeliveryInfo";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { getRelativeRootOnLocalhost } from "@danishagro/shared/src/helpers/getRelativeUrlOnLocalhost.helper";
import { B2C_SITE_ORIGIN } from "@danishagro/shared/src/constants/urlRoot.constants";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { useFavorites } from "@contexts/favorites.context";
import toast from "react-hot-toast";
import { DA_Toast } from "@danishagro/shared/src/components/molecules/Toast/Toast.component";
import { adjustUrl } from "@danishagro/shared/src/helpers/adjustUrl.helper";
import { DA_Tag } from "@danishagro/shared/src/components/atoms/Tag/Tag.component";
import { DA_Image } from "@danishagro/shared/src/components/atoms/Image/Image.component";
import { useMaxValue } from "@hooks/useMaxValue.hook";
import { useRecaptcha } from "@danishagro/shared/src/hooks/useRecaptcha.hook";
import { RECAPTCHA_CONFIG } from "@danishagro/shared/src/config/recaptcha.config";
import { DA_ProductListPrice } from "./components/ProductListPrice/ProductListPrice.component";
import S from "./ProductListItemV1.module.scss";

export type V1Props = DA_NewProductSimple & {
    variantGroups: VariantGroup[];
    eagerLoading?: boolean;
    fetchPriority?: boolean;
    "data-id"?: string;
    "data-name"?: string;
    "data-listid"?: string;
    "data-price"?: string;
    "data-category"?: string;
    "data-brand"?: string;
};

export const V1 = React.forwardRef<HTMLElement, V1Props>((props, ref) => {
    const {
        hasVariants,
        variantEntityId,
        displayName,
        newProduct,
        imageUrl,
        eagerLoading,
        url,
        hasHazardStatement,
        quantityOnPallet,
    } = props;

    // SoldOnline
    const getRelewiseDataField = (fieldName: string): string | undefined => {
        const value =
            props.relewiseProduct?.variant?.data?.[fieldName]?.value ??
            props.relewiseProduct?.allVariants?.[0]?.data?.[fieldName]?.value;
        return typeof value === "string" ? value.trim() : undefined;
    };
    const soldOnline = getRelewiseDataField("soldOnline");
    const onlySoldOnline = getRelewiseDataField("onlySoldOnline");
    const isSoldOnline = soldOnline === "Ja" || !soldOnline; // same logic as before
    const isOnlySoldOnline = onlySoldOnline === "Ja";

    const hasSpecialFreightInformation =
        props.relewiseProduct?.allVariants?.[0]?.data?.specialFreightInformation?.value;
    const specialFreightInformationText =
        props.relewiseProduct?.allVariants?.[0]?.data?.specialFreightDescription?.value;

    const navigate = useNavigate();
    const [quantity, setQuantity] = useState(1);
    const [awaitingResponse, setAwaitingResponse] = useState(false);

    const { showModal } = useModal();
    const { showOverlay } = useDynamicOverlay();
    const { isMobileOrTablet } = useScreen();
    const { getDictionaryString } = useTranslations();

    // TODO: Get this from New cart context
    const { addToBasket } = useBasket();
    const { currentSite } = useAppData();

    const [choosedValue, setChoosedValue] = useState(false);
    const { presentBottomSheet } = useBottomSheet();
    const { siteVariant } = useAppData();
    const [modalViewState, setModalViewState] = useState<modalState>(modalState.hide);
    const [numberOfPallets, setNumberOfPallets] = useState<number>(0);
    const [totalPalletQuantity, setTotalPalletQuantity] = useState<number>(0);

    const isAvailableToBuy = useRef(!props.data?.redirectUrl?.value).current;
    const highMax = useMaxValue(siteVariant);
    const deliveryModalTitle = getDictionaryString(
        hasSpecialFreightInformation ? "deliveryInfo.infoOverwrite" : "deliveryInfo.info"
    );
    const favoritesListUrl = `${getRelativeRootOnLocalhost(B2C_SITE_ORIGIN)}/favorites`;

    // reCAPTCHA v3 integration
    const { executeRecaptcha, isReady: isRecaptchaReady } = useRecaptcha({
        siteKey: RECAPTCHA_CONFIG.SITE_KEY,
        enabled: RECAPTCHA_CONFIG.ENABLED,
    });

    const {
        addFavorite,
        removeFavorite,
        isFavoriteProduct,
        loading: favoritesLoading,
    } = useFavorites();

    const productVariantIds =
        props.relewiseProduct?.allVariants?.map((variant) => variant.variantId) ?? [];
    const isFavorite = isFavoriteProduct(
        {
            entityId: props.entityId,
            variantEntityId: props.variantEntityId,
            hasVariants: props.hasVariants,
            variantGroups: [],
        } as DA_NewProduct,
        productVariantIds
    );

    // Shipping modal
    const openShippingInfoModal = useCallback(() => {
        showModal(
            <DA_DeliveryInfoModalContent
                specialFreightOverlayText={specialFreightInformationText}
            />,
            {
                size: ModalSize.XXL,
            }
        );
    }, [showModal, specialFreightInformationText]);

    const openDynamicOverlay = useCallback(() => {
        showOverlay({
            id: "delivery-filter_open",
            title: deliveryModalTitle,
            closeButtonBottom: true,
            content: (
                <BottomSheetDeliveryInfo
                    version="v1"
                    specialFreightOverlayText={specialFreightInformationText}
                />
            ),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOverlay]);

    const formatPrice = useCallback(
        (price: number) => formatNumber(price, "da-DK", { decimals: 2 }),
        []
    );

    const {
        "data-id": dataId,
        "data-name": dataName,
        "data-listid": dataListId,
        "data-category": dataCategory,
        "data-brand": dataBrand,
        "data-price": dataPrice,
        // ... rest of your props
    } = props;

    // Extract the category from the URL
    const getCategoryFromUrl = (url: string = "") =>
        (() => {
            try {
                return new URL(url).pathname.split("/").filter(Boolean).slice(0, -1).join("_");
            } catch {
                return url.split("/").filter(Boolean).slice(0, -1).join("_");
            }
        })();

    const category = getCategoryFromUrl(props.url);

    const capitalizeFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

    const getLastCategoryTag = (category: string = "") =>
        category.split("_").filter(Boolean).pop() ?? "";

    const itemListName = capitalizeFirst(getLastCategoryTag(category));

    // GTM Tracking product clicked
    const trackProductClick = useProductTracker({
        ...props,
        price: formatPrice(Number((dataPrice || props?.price) ?? 0)),
        currency: props?.price?.currency || "DKK",
        quantity: quantity,
        category: category,
        name: displayName,
        brand: dataBrand || props.brand,
        listName: itemListName,
    });

    const latestQuantityRef = useRef(quantity);
    const quantityAsInt = parseInt(quantityOnPallet?.value);

    useEffect(() => {
        latestQuantityRef.current = quantity;
    }, [quantity]);

    const calculateRangeOffset = (quantityOnPallet: number) => {
        return Math.floor(quantityOnPallet * 0.25); // ±25% of quantityOnPallet
    };

    useEffect(() => {
        const intValue = quantity;
        setModalViewState(modalState.hide);

        if (!isNaN(quantity)) {
            const rangeOffset = calculateRangeOffset(quantityAsInt);
            for (let numberOfPallets = 1; numberOfPallets <= 100; numberOfPallets++) {
                const totalPalletQuantity = numberOfPallets * quantityAsInt;
                const lowerBound = totalPalletQuantity - rangeOffset || 0;
                const upperBound = totalPalletQuantity + rangeOffset || 0;

                if (
                    intValue !== totalPalletQuantity &&
                    intValue >= lowerBound &&
                    intValue <= upperBound
                ) {
                    setModalViewState(modalState.show);
                    setNumberOfPallets(numberOfPallets);
                    setTotalPalletQuantity(totalPalletQuantity);
                    return;
                } else if (intValue === totalPalletQuantity) {
                    setModalViewState(modalState.hide);
                    return;
                }
            }
        }
    }, [quantity, quantityAsInt]);

    const check = useCallback(
        (
            showModalState: modalState,
            defaultQuantityOnPallet: number,
            totalPalletQuantity: number,
            numberOfPallets: number
        ) => {
            return new Promise<number>((resolve) => {
                if (showModalState === modalState.show && !choosedValue) {
                    const handleSelection = (selectedValue) => {
                        setQuantity(selectedValue);
                        setChoosedValue(true);
                        resolve(selectedValue); // Resolve the promise after the state is updated with the new quantity
                    };

                    if (isMobileOrTablet) {
                        presentBottomSheet(
                            <DA_QuantityOnPalletOverlay
                                quantity={latestQuantityRef.current}
                                quantityOnPallet={totalPalletQuantity}
                                defaultQuantityOnPallet={defaultQuantityOnPallet}
                                numberOfPallets={numberOfPallets}
                                onSelection={handleSelection}
                            />,
                            {
                                id: "shop-quantity-bottom-sheet",
                                contentPadding: true,
                                headerTitle: getDictionaryString("shopquantityonpallettitle"),
                                snapPoints: ({ minHeight }) => [minHeight],
                                hideCloseButton: true,
                            }
                        );
                    } else {
                        showModal(
                            <DA_Modal title={getDictionaryString("shopquantityonpallettitle")}>
                                <DA_QuantityOnPalletOverlay
                                    quantity={latestQuantityRef.current}
                                    quantityOnPallet={totalPalletQuantity}
                                    defaultQuantityOnPallet={quantityAsInt}
                                    numberOfPallets={numberOfPallets}
                                    onSelection={handleSelection}
                                />
                            </DA_Modal>,
                            {
                                size: ModalSize.XS,
                                shouldCloseOnEsc: false,
                                shouldCloseOnOverlayClick: false,
                            }
                        );
                    }
                } else {
                    resolve(latestQuantityRef.current); // Resolve immediately if no modal is shown
                }
            });
        },
        [
            choosedValue,
            getDictionaryString,
            isMobileOrTablet,
            presentBottomSheet,
            quantityAsInt,
            showModal,
        ]
    );

    // Navigate to the variant that has the lowest discount
    const discountedVariantUrl = useMemo(() => {
        const variantId = props.relewiseProduct?.data?.lowestDiscountVariant?.value;
        if (!variantId) return null;

        const baseUrl = props.relewiseProduct?.data?.canonicalBaseUrl?.value;
        if (!baseUrl) return null;

        const slug = props.relewiseProduct?.allVariants?.find((v) => v.variantId === variantId)
            ?.data?.webNameSlug?.value;

        return slug ? adjustUrl(`${baseUrl}/${slug}`) : null;
    }, [
        props.relewiseProduct?.data?.lowestDiscountVariant?.value,
        props.relewiseProduct?.data?.canonicalBaseUrl?.value,
        props.relewiseProduct?.allVariants,
    ]);

    const productUrl = discountedVariantUrl ?? adjustUrl(url, currentSite);

    const addProductToCart = useCallback(
        async (modalViewState, defaultQuantityOnPallet, totalPalletQuantity, numberOfPallets) => {
            const selectedQuantity = await check(
                modalViewState,
                defaultQuantityOnPallet,
                totalPalletQuantity,
                numberOfPallets
            ); // Wait for check to complete and get the latest quantity

            setAwaitingResponse(true);

            try {
                // Execute reCAPTCHA v3 for add_to_cart action (for analytics/monitoring, not backend verification)
                if (RECAPTCHA_CONFIG.ENABLED && isRecaptchaReady) {
                    try {
                        await executeRecaptcha(RECAPTCHA_CONFIG.ACTIONS.ADD_TO_CART);
                    } catch (error) {
                        console.warn("reCAPTCHA execution failed:", error);
                        // Continue with add to cart even if reCAPTCHA fails
                    }
                }

                if (hasHazardStatement || hasVariants) {
                    navigate(discountedVariantUrl ?? url);
                } else {
                    await addToBasket(
                        {
                            productId: variantEntityId,
                            quantity: selectedQuantity,
                            productName: dataName || props.displayName,
                        },
                        {
                            showToast: isMobileOrTablet,
                        }
                    );
                }

                // GTM Tracking
                trackProductClick("add_to_cart");
            } catch (error) {
                console.error("Error adding product to cart:", error);
                // Handle error appropriately - maybe show a user-friendly message
            } finally {
                setAwaitingResponse(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            addToBasket,
            check,
            executeRecaptcha,
            hasHazardStatement,
            hasVariants,
            isRecaptchaReady,
            navigate,
            trackProductClick,
            url,
            variantEntityId,
            isMobileOrTablet,
        ]
    );

    const onLinkClicked = () => {
        if (typeof window !== "undefined") {
            // Save scroll position to sessionStorage for restoration when returning via back button
            const scrollY =
                window.pageYOffset ||
                document.documentElement.scrollTop ||
                document.body.scrollTop ||
                0;
            const currentPage = new URLSearchParams(window.location.search).get("page") || "1";

            const scrollData = {
                scrollY: scrollY,
                fromPage: currentPage,
                url: window.location.pathname + window.location.search,
                timestamp: Date.now(),
            };

            sessionStorage.setItem("categoryScrollPosition", JSON.stringify(scrollData));
        }
        // // GTM Tracking the product click when the link is clicked
        trackProductClick("select_item");
    };

    const handleFavoriteToggle = useCallback(() => {
        const action = isFavorite ? removeFavorite : addFavorite;
        const toastText = isFavorite
            ? getDictionaryString("toast.removedFromFavoritesText")
            : getDictionaryString("toast.addedToFavoritesText");

        action(props.entityId, props.variantEntityId).then(() => {
            toast.custom(() => (
                <DA_Toast
                    href={favoritesListUrl}
                    goTo={getDictionaryString("toast.link.showFavorites")}
                    altStyle={true}
                    message={
                        <>
                            <div>{toastText}</div>
                            <div className="break-all">{props.webName}</div>
                        </>
                    }
                />
            ));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFavorite, props.entityId, props.variantEntityId]);

    const calculatePrice = useCallback(() => {
        if (props.price?.quantityDiscounts?.length > 0) {
            const applicableDiscount = props.price.quantityDiscounts.find(
                (item) => quantity >= item.quantityFrom && quantity <= (item.quantityTo || Infinity)
            );
            return applicableDiscount ? applicableDiscount.price : props.price.priceWithoutVat || 0;
        }
        return props.price?.priceWithoutVat || 0;
    }, [props.price, quantity]);

    const calculatedPrice = calculatePrice();

    return (
        <article
            className={cn(S.productListItem, newProduct && S.productListItemNew)}
            ref={ref}
            data-id={dataId}
            data-category={dataCategory}
            data-brand={dataBrand}
            key={variantEntityId}
            data-name={dataName}
            data-listid={dataListId}
            data-price={formatPrice(calculatedPrice)}
        >
            <div className={S.imageWrapper}>
                {props.hasDiscount && (
                    <div className={S.discountTag}>
                        <DA_Tag color="yellow" size="large">
                            {getDictionaryString("general.onSale")}
                        </DA_Tag>
                    </div>
                )}

                <div className={cn(S.favBtn, isFavorite && S.isFavorite)}>
                    {hasVariants ? (
                        // If the product has variants, show the link to the favorites list
                        isFavorite ? (
                            <DA_Button
                                href={favoritesListUrl}
                                icon={DA_IconNames.HeartSolid}
                                disabled={favoritesLoading}
                                ariaLabel={getDictionaryString(
                                    "accessibility.screenReader.addProductToFavoritList",
                                    {
                                        product: displayName,
                                    }
                                )}
                            />
                        ) : (
                            <DA_Button
                                href={productUrl}
                                icon={DA_IconNames.HeartOutline}
                                disabled={favoritesLoading}
                                ariaLabel={getDictionaryString(
                                    "accessibility.screenReader.removeProductFromFavoritList",
                                    {
                                        product: displayName,
                                    }
                                )}
                            />
                        )
                    ) : (
                        // If the product does not have variants, show a button to add/remove it directly
                        <DA_Button
                            onClick={handleFavoriteToggle}
                            icon={isFavorite ? DA_IconNames.HeartSolid : DA_IconNames.HeartOutline}
                            disabled={favoritesLoading}
                            ariaLabel={
                                isFavorite
                                    ? `${getDictionaryString(
                                          "accessibility.screenReader.removeProductFromFavoritList",
                                          {
                                              product: displayName,
                                          }
                                      )}`
                                    : `${getDictionaryString(
                                          "accessibility.screenReader.addProductToFavoritList",
                                          {
                                              product: displayName,
                                          }
                                      )}`
                            }
                        />
                    )}
                </div>

                <DA_BasicLink
                    href={productUrl}
                    className={S.imageLink}
                    onClick={onLinkClicked}
                    ariaLabel={displayName}
                    tabIndex={-1}
                >
                    {
                        <div className={S.productImageWrapper}>
                            {/* TODO: Develop new image component */}
                            <DA_Image
                                src={B2bImageSrc(imageUrl, ImageConfig.CategoryImage)}
                                alt={dataName}
                                loading={eagerLoading ? "eager" : "lazy"}
                                fetchpriority={props.fetchPriority ? "high" : "auto"}
                                className={S.productImage}
                            />
                        </div>
                    }
                </DA_BasicLink>

                {isOnlySoldOnline && (
                    <div className={S.soldByTag}>
                        <DA_Tag color="good" size="large">
                            {getDictionaryString("tags.onlineOnly")}
                        </DA_Tag>
                    </div>
                )}
                {!isSoldOnline && (
                    <div className={S.soldByTag}>
                        <DA_Tag color="grey" size="large">
                            {getDictionaryString("tags.storeOnly")}
                        </DA_Tag>
                    </div>
                )}
            </div>

            <div className={S.productDataWrapper}>
                <div>
                    <div className={S.productNumber}>
                        <DA_Text noPadding smallText>
                            {`${getDictionaryString("productCard.productNumber")} ${
                                props.productNumber
                            }`}
                        </DA_Text>
                    </div>
                    <div className={S.productName}>
                        <DA_BasicLink href={productUrl} className={S.productNameLink}>
                            <span>{displayName}</span>
                        </DA_BasicLink>
                    </div>
                    {hasVariants && (
                        <div className={S.lightText}>
                            {getDictionaryString("productCard.moreVariants")}
                        </div>
                    )}
                </div>

                <div className={S.secondaryContent}>
                    {/** Energy Classification */}
                    {props.data?.energyEfficiencyArrows?.value &&
                        props.data?.energyEfficiencyReport?.value && (
                            <div className={S.energyWrapper}>
                                <DA_BasicLink href={productUrl}>
                                    <img
                                        className={S.energyImage}
                                        src={props.data?.energyEfficiencyArrows?.value}
                                        alt={props.data?.energyEfficiencyReport.value}
                                    />
                                </DA_BasicLink>
                                <DA_InlineLink
                                    className={S.energyLabel}
                                    href={props.data?.energyEfficiencyReport?.value}
                                    title={getDictionaryString("productCard.productSheet")}
                                    target="_blank"
                                />
                            </div>
                        )}

                    {/** Price */}
                    {isAvailableToBuy && (
                        <div>
                            <DA_ProductListPrice
                                quantity={quantity}
                                listPrice={props.listPrice}
                                salesPrice={props.salesPrice}
                                hasVariants={hasVariants}
                                quantityDiscounts={props.data?.quantityDiscounts}
                                onClick={
                                    isMobileOrTablet ? openDynamicOverlay : openShippingInfoModal
                                }
                                onQuantityChanged={(qty: number) => {
                                    setQuantity(qty);
                                }}
                            />
                        </div>
                    )}

                    {!isSoldOnline ? (
                        <DA_Button
                            href={productUrl}
                            title={getDictionaryString("readMore")}
                            fullWidth
                        />
                    ) : (
                        <div className={S.pushContent}>
                            {/** Quantity */}
                            {isAvailableToBuy ? (
                                <DA_QuantityPicker
                                    value={quantity}
                                    onChange={setQuantity}
                                    compactOnMobile
                                    higherMax={highMax}
                                />
                            ) : null}

                            {/** Button */}
                            <DA_CartButton
                                hasVariants={hasVariants}
                                hasHazardStatements={hasHazardStatement}
                                isAvailableToBuy={isAvailableToBuy}
                                productPage={productUrl}
                                hideText={isMobileOrTablet}
                                addToCart={() =>
                                    addProductToCart(
                                        modalViewState,
                                        quantityAsInt,
                                        totalPalletQuantity,
                                        numberOfPallets
                                    )
                                }
                                showSpinner={awaitingResponse}
                                fullWidth={isMobileOrTablet}
                                className={S.cartSliderBuyBtn}
                                ariaTitle={displayName}
                            />
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
});

V1.displayName = "V1";
